import { Trans, useLingui } from "@lingui/react/macro";
import type { ThinkingLevel } from "@rakazo/contracts";
import { toggleEnabledModelId } from "@rakazo/core";
import {
  Button,
  Checkbox,
  Disclosure,
  Input,
  Label,
  ModelThinkingOptions,
  NativeSelect,
  NativeSelectOption,
} from "@rakazo/ui-web";
import { Image as ImageIcon } from "lucide-react";
import { ModelPreflightFeedback } from "../../components/ModelConnectionPreflightNotice";

import type { ModelSettingsState } from "./use-model-settings";

export function CompatibleModelFields({
  baseUrl,
  busy,
  cancelOAuthAttempt,
  compatibleRows,
  contextWindow,
  defaultModelOptions,
  editSettingsFor,
  effectiveBaseUrl,
  enabledModelIds,
  invalidatePreflight,
  maxImagesPerPrompt,
  maxTokens,
  modelId,
  preflightFailure,
  preflightSuccess,
  preflightTarget,
  preflightTesting,
  probeModels,
  probeServerModels,
  probing,
  reasoning,
  selectionRevisionRef,
  setContextWindow,
  setEnabledModelIds,
  setError,
  setMaxImagesPerPrompt,
  setMaxTokens,
  setModelImages,
  setNotice,
  setReasoning,
  setSupportsImages,
  setThinkingLevel,
  settingsModelId,
  stageCompatibleModelId,
  supportsImages,
  testCompatibleConnection,
  thinkingLevel,
  updateBaseUrl,
  visionModelIds,
}: Pick<
  ModelSettingsState,
  | "baseUrl"
  | "busy"
  | "cancelOAuthAttempt"
  | "compatibleRows"
  | "contextWindow"
  | "defaultModelOptions"
  | "editSettingsFor"
  | "effectiveBaseUrl"
  | "enabledModelIds"
  | "invalidatePreflight"
  | "maxImagesPerPrompt"
  | "maxTokens"
  | "modelId"
  | "preflightFailure"
  | "preflightSuccess"
  | "preflightTarget"
  | "preflightTesting"
  | "probeModels"
  | "probeServerModels"
  | "probing"
  | "reasoning"
  | "selectionRevisionRef"
  | "setContextWindow"
  | "setEnabledModelIds"
  | "setError"
  | "setMaxImagesPerPrompt"
  | "setMaxTokens"
  | "setModelImages"
  | "setNotice"
  | "setReasoning"
  | "setSupportsImages"
  | "setThinkingLevel"
  | "settingsModelId"
  | "stageCompatibleModelId"
  | "supportsImages"
  | "testCompatibleConnection"
  | "thinkingLevel"
  | "updateBaseUrl"
  | "visionModelIds"
>) {
  const { t } = useLingui();
  const testingCompatible = preflightTesting && preflightTarget === "compatible";
  const defaultModelId = modelId.trim();
  // Every enabled model has its own settings; the effort level is the space default's alone.
  const settingsChoices = [
    ...new Set([defaultModelId, ...enabledModelIds].filter((id) => id.length > 0)),
  ];
  const editingDefault = settingsModelId === defaultModelId;
  return (
    <div className="block text-[13.5px] text-muted-foreground">
      <label className="block" htmlFor="model-base-url">
        <Trans>Server URL</Trans>
        <Input
          id="model-base-url"
          value={baseUrl}
          onChange={(event) => updateBaseUrl(event.target.value)}
          aria-label={t`OpenAI-compatible server URL`}
          placeholder="http://127.0.0.1:8000/v1"
          autoComplete="off"
          className="mt-2 h-10 text-foreground"
        />
      </label>
      <Disclosure
        className="mt-2 text-[13px] leading-[1.5] text-muted-foreground"
        summary={<Trans>Setup help</Trans>}
      >
        <p className="mt-1">
          {t`Paste the OpenAI-compatible address from your server. HrokBot adds /v1 if needed.`}
        </p>
      </Disclosure>
      <div className="mt-3 flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || probing || !effectiveBaseUrl}
          onClick={() => void probeServerModels()}
        >
          {probing ? <Trans>Finding…</Trans> : <Trans>Find models</Trans>}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || probing || testingCompatible || !effectiveBaseUrl || !modelId.trim()}
          onClick={() => void testCompatibleConnection()}
        >
          {testingCompatible ? <Trans>Testing…</Trans> : <Trans>Test connection</Trans>}
        </Button>
      </div>
      {preflightTarget === "compatible" ? (
        <ModelPreflightFeedback success={preflightSuccess} failure={preflightFailure} />
      ) : null}
      <div className="mt-4 block">
        <span>
          <Trans>Model</Trans>
        </span>
        {compatibleRows.includes(modelId) ? (
          <NativeSelect
            className="mt-2 w-full text-foreground"
            value={modelId}
            onChange={(event) => {
              cancelOAuthAttempt();
              selectionRevisionRef.current += 1;
              invalidatePreflight();
              stageCompatibleModelId(event.target.value);
              setError(null);
              setNotice(null);
            }}
            aria-label={t`Models from server`}
          >
            {defaultModelOptions.map((id) => (
              <NativeSelectOption key={id} value={id}>
                {id}
              </NativeSelectOption>
            ))}
            <NativeSelectOption value="">
              <Trans>Other model…</Trans>
            </NativeSelectOption>
          </NativeSelect>
        ) : (
          <Input
            value={modelId}
            onChange={(event) => {
              cancelOAuthAttempt();
              selectionRevisionRef.current += 1;
              invalidatePreflight();
              stageCompatibleModelId(event.target.value);
              setError(null);
              setNotice(null);
            }}
            aria-label={t`Model id`}
            placeholder="exact-model-id"
            className="mt-2 h-10 text-foreground"
          />
        )}
        {compatibleRows.length ? (
          <div className="mt-3 grid gap-2">
            {compatibleRows.map((id) => {
              const enabled = id === modelId || enabledModelIds.includes(id);
              return (
                <div key={id} className="flex items-center gap-3">
                  <Label className="min-w-0 flex-1 font-normal text-foreground">
                    <Checkbox
                      checked={enabled}
                      disabled={busy || id === modelId}
                      onCheckedChange={(checked) => {
                        selectionRevisionRef.current += 1;
                        setEnabledModelIds((current) => toggleEnabledModelId(current, id, checked));
                        setNotice(null);
                      }}
                    />
                    <span className="min-w-0 break-all">{id}</span>
                  </Label>
                  {enabled ? (
                    <Label className="shrink-0 font-normal" title={t`Supports images`}>
                      <Checkbox
                        aria-label={`${t`Supports images`}: ${id}`}
                        checked={visionModelIds.includes(id)}
                        disabled={busy}
                        onCheckedChange={(checked) => {
                          selectionRevisionRef.current += 1;
                          setModelImages(id, checked);
                          setNotice(null);
                        }}
                      />
                      <ImageIcon className="size-4" aria-hidden />
                    </Label>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
        {probeModels.length && !compatibleRows.includes(modelId) ? (
          <Button
            type="button"
            variant="link"
            className="mt-2 h-auto px-0 text-[13px] leading-[1.5] text-muted-foreground underline"
            onClick={() => {
              invalidatePreflight();
              stageCompatibleModelId(probeModels[0] ?? "");
            }}
          >
            <Trans>Use a found model</Trans>
          </Button>
        ) : null}
      </div>
      <ModelThinkingOptions
        reasoning={reasoning}
        onReasoningChange={(value) => {
          selectionRevisionRef.current += 1;
          setReasoning(value);
          if (!value) setThinkingLevel(null);
          setNotice(null);
        }}
        disabled={busy}
        advancedLabel={t`Advanced`}
        thinkingLabel={t`Supports thinking`}
        thinkingLevel={thinkingLevel}
        onThinkingLevelChange={
          editingDefault
            ? (value) => {
                selectionRevisionRef.current += 1;
                setThinkingLevel(value as ThinkingLevel | null);
                setNotice(null);
              }
            : undefined
        }
        header={
          settingsChoices.length > 1 ? (
            <label className="mt-3 block" htmlFor="model-settings-for">
              <Trans>Settings for</Trans>
              <NativeSelect
                id="model-settings-for"
                className="mt-2 w-full"
                value={settingsModelId}
                disabled={busy}
                onChange={(event) => editSettingsFor(event.target.value)}
                aria-label={t`Settings for`}
              >
                {settingsChoices.map((id) => (
                  <NativeSelectOption key={id} value={id}>
                    {id}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </label>
          ) : null
        }
        thinkingLevelOptions={[
          { value: "minimal", label: t`Minimal` },
          { value: "low", label: t`Low` },
          { value: "medium", label: t`Medium` },
          { value: "high", label: t`High` },
          { value: "xhigh", label: t`Extra high` },
          { value: "max", label: t`Max` },
        ]}
        thinkingLevelLabel={t`Reasoning effort`}
        thinkingLevelDefaultLabel={t`Default`}
        maxTokens={maxTokens}
        onMaxTokensChange={(value) => {
          selectionRevisionRef.current += 1;
          setMaxTokens(value);
          setNotice(null);
        }}
        maxTokensLabel={t`Maximum output tokens`}
        contextWindow={contextWindow}
        onContextWindowChange={(value) => {
          selectionRevisionRef.current += 1;
          setContextWindow(value);
          setNotice(null);
        }}
        contextWindowLabel={t`Context limit`}
        supportsImages={supportsImages}
        onSupportsImagesChange={(value) => {
          selectionRevisionRef.current += 1;
          setSupportsImages(value);
          setNotice(null);
        }}
        imagesLabel={t`Supports images`}
        maxImagesPerPrompt={maxImagesPerPrompt}
        onMaxImagesPerPromptChange={(value) => {
          selectionRevisionRef.current += 1;
          setMaxImagesPerPrompt(value);
          setNotice(null);
        }}
        maxImagesLabel={t`Maximum images per request`}
      />
    </div>
  );
}
